import React, { useEffect, useState } from 'react';
import { CloseIcon } from '@/components/icons';
import { Loader } from '@/components/ui';
import type { VideoItem } from '@/lib/api/types';
import Modal from '@/pages/multimedia/videos/modals/Modal';

interface VideoPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  video: VideoItem;
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({ isOpen, onClose, video }) => {
  const [embedUrl, setEmbedUrl] = useState<string>('');

  useEffect(() => {
    const convertToEmbedUrl = (url: string): string => {
      if (url.includes('youtube.com/embed/')) return url;

      let videoId: string | null = null;
      const watchMatch = url.match(/(?:youtube\.com\/watch\?v=)([^&?/\n]+)/);
      if (watchMatch?.[1]) videoId = watchMatch[1];

      if (!videoId) {
        const shortMatch = url.match(/(?:youtu\.be\/)([^&?/\n]+)/);
        if (shortMatch?.[1]) videoId = shortMatch[1];
      }

      if (!videoId) {
        const embedMatch = url.match(/(?:youtube\.com\/embed\/)([^&?/\n]+)/);
        if (embedMatch?.[1]) videoId = embedMatch[1];
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    };

    setEmbedUrl(convertToEmbedUrl(video.embeddedLink));
  }, [video.embeddedLink]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-4xl">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">{video.title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
          {embedUrl ? (
            <iframe
              src={embedUrl}
              title={video.title}
              className="w-full h-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Loader size="lg" variant="white" />
            </div>
          )}
        </div>
        <p className="text-sm text-gray-600">{video.description}</p>
      </div>
    </Modal>
  );
};

export default VideoPlayerModal;
